import { useState } from 'react';

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'Can you really buy my house if it has title issues?',
      answer: 'Yes! We specialize in buying houses with title issues in El Paso. We\'ve purchased over 200 properties with liens, clouded titles, probate complications, and boundary disputes. As cash buyers, we don\'t need mortgage approval, which means we can close even when traditional buyers can\'t.'
    },
    {
      question: 'How do you pay off liens at closing?',
      answer: 'When we purchase your property, the title company handles all lien payoffs at closing. The liens are paid directly from the sale proceeds before you receive your money. For example, if we buy your house for $150,000 and there are $20,000 in liens, the title company pays the liens and you receive $130,000. You never have to come out of pocket.'
    },
    {
      question: 'What if I can\'t afford to pay the liens myself?',
      answer: 'You don\'t have to! That\'s the whole point of selling to us. We structure the deal so all liens are paid at closing from the proceeds. You never need to pay anything out of pocket. This is especially helpful for heirs who inherit properties with liens they didn\'t create and can\'t afford to pay.'
    },
    {
      question: 'How long does it take to close on a house with title issues?',
      answer: 'For properties where you have clear authority to sell, we can close in 7-14 days. If probate is pending, we can give you an offer now and wait for probate to complete before closing. The actual closing process is fast once all legal requirements are met.'
    },
    {
      question: 'What if probate isn\'t complete yet?',
      answer: 'We can work with you even if probate is pending. We\'ll evaluate the property and give you a cash offer now. Then we\'ll wait for your Letters of Administration or Letters Testamentary to be issued. Once you have legal authority to sell, we can close within days. This gives you certainty during an uncertain time.'
    },
    {
      question: 'Can you help if multiple heirs own the property?',
      answer: 'Absolutely. We\'ve successfully closed deals with 2, 3, 4, and even more co-owners. We can structure deals that satisfy all parties, or we can purchase fractional interests from individual heirs. We\'ve even helped families where siblings were estranged and hadn\'t spoken in years.'
    },
    {
      question: 'What if one heir won\'t agree to sell?',
      answer: 'We have several options. We can try to negotiate with the holdout heir to find a solution that works for everyone. We can purchase fractional interests from the heirs who want to sell. Or we can help you understand your options for partition lawsuits if necessary. Every situation is different, and we\'ll work with you to find the best solution.'
    },
    {
      question: 'Do you buy houses with boundary disputes?',
      answer: 'Yes! We\'re willing to accept boundary risks that traditional buyers and mortgage lenders won\'t touch. We can purchase properties with encroachments, easement disputes, survey issues, and unresolved boundary conflicts. We\'ll handle the resolution process after closing.'
    },
    {
      question: 'What is a quiet title action and do I need one?',
      answer: 'A quiet title action is a lawsuit that clears competing claims to property ownership. You might need one if there are unresolved liens, missing heirs, boundary disputes, or other title defects. However, when you sell to us, you typically don\'t need to file a quiet title action yourself. We can purchase the property and handle the quiet title action after closing if necessary.'
    },
    {
      question: 'How much will you offer for my house with title issues?',
      answer: 'Our offers are based on the property\'s condition, location, and the complexity of the title issues. We account for the work we\'ll need to do to resolve the problems, but we still make competitive offers. The best way to find out is to call us for a free consultation. We\'ll evaluate your situation and give you a fair cash offer within 24-48 hours.'
    },
    {
      question: 'Are there any fees or commissions?',
      answer: 'No! We don\'t charge any fees or commissions. There are no real estate agent commissions (typically 6%), no closing costs, and no hidden fees. The offer we make is the amount you\'ll receive (minus any liens that need to be paid off at closing).'
    },
    {
      question: 'What if the property is in really bad condition?',
      answer: 'We buy houses in any condition. Inherited properties are often neglected for years, and we understand that. We don\'t require any repairs, cleaning, or improvements. We buy as-is, which means you can sell without spending a dime on the property.'
    },
    {
      question: 'Can you buy if there are back taxes owed?',
      answer: 'Yes! Back taxes are just another type of lien that we handle at closing. We\'ve purchased properties with $20,000+ in back taxes. The taxes are paid from the sale proceeds at closing, so you don\'t have to pay them yourself.'
    },
    {
      question: 'What if I don\'t know all the liens on the property?',
      answer: 'That\'s common with inherited properties. We work with our title company to conduct a thorough title search that identifies all liens and encumbrances. We\'ll find everything and handle it all at closing. You don\'t need to know what\'s there - we\'ll figure it out.'
    },
    {
      question: 'How do I know this isn\'t a scam?',
      answer: 'Great question! We close all transactions through reputable title companies, never through us directly. The title company is a neutral third party that protects both buyer and seller. We\'re a legitimate real estate investment company that\'s been buying houses in El Paso for years. We can provide references from past sellers and proof of funds. We encourage you to do your due diligence before working with any cash buyer.'
    },
    {
      question: 'What happens after I accept your offer?',
      answer: 'Once you accept our offer, we open escrow with a title company. The title company conducts a title search to identify all issues. We work with them to resolve any problems. When everything is ready, we schedule a closing date. At closing, you sign the paperwork, all liens are paid off, and you receive your cash. The whole process is handled professionally through the title company.'
    },
    {
      question: 'Can you help if the property is in foreclosure?',
      answer: 'Yes! If the property is facing foreclosure, time is critical. We can often close quickly enough to stop the foreclosure and pay off the mortgage. Even if foreclosure proceedings have started, we may be able to help. Call us immediately if foreclosure is a concern - every day matters.'
    },
    {
      question: 'What if there\'s a mortgage on the property?',
      answer: 'No problem. The mortgage is paid off at closing just like any other lien. If the property is worth more than the mortgage balance, you receive the difference. If the property is underwater (mortgage exceeds value), we can sometimes negotiate a short sale with the lender.'
    }
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Frequently Asked <strong>Questions</strong>
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Get answers to common questions about selling houses with title issues in El Paso
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <span className="font-semibold text-lg text-gray-900 pr-8">{faq.question}</span>
                <i className={`ri-arrow-${openIndex === index ? 'up' : 'down'}-s-line text-2xl text-emerald-600 flex-shrink-0`}></i>
              </button>
              {openIndex === index && (
                <div className="px-6 pb-5">
                  <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 bg-emerald-500 text-white rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold mb-4">Still Have Questions?</h3>
          <p className="text-lg mb-6">
            Every title issue situation is unique. Call us for a free consultation and we'll answer all your questions.
          </p>
          <a 
            href="tel:9152065814"
            className="inline-block bg-white text-emerald-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-xl whitespace-nowrap cursor-pointer"
          >
            Call (915) 206-5814
          </a>
        </div>
      </div>
    </section>
  );
}